import { useEffect } from 'react';
import type { Editor } from '@tiptap/core';

/** Intercepts paste and drop events to upload image files and insert them via setImage. */
export function useImagePaste(
  editor: Editor | null,
  onImageUpload?: (file: File) => Promise<string>,
) {
  useEffect(() => {
    if (!editor || !onImageUpload) return;

    const handlePaste = (event: ClipboardEvent) => {
      const items = Array.from(event.clipboardData?.items ?? []);
      const imageItem = items.find((item) => item.type.startsWith('image/'));
      if (!imageItem) return;

      event.preventDefault();
      const file = imageItem.getAsFile();
      if (!file) return;

      void onImageUpload(file).then((url) => {
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      });
    };

    const handleDrop = (event: DragEvent) => {
      const files = Array.from(event.dataTransfer?.files ?? []);
      const imageFile = files.find((f) => f.type.startsWith('image/'));
      if (!imageFile) return;

      event.preventDefault();
      void onImageUpload(imageFile).then((url) => {
        editor.chain().focus().setImage({ src: url, alt: imageFile.name }).run();
      });
    };

    const dom = editor.view.dom;
    dom.addEventListener('paste', handlePaste);
    dom.addEventListener('drop', handleDrop);

    return () => {
      dom.removeEventListener('paste', handlePaste);
      dom.removeEventListener('drop', handleDrop);
    };
  }, [editor, onImageUpload]);
}
